import React from 'react';
import { User } from '../apiClients/workspace/types';
import { useLanguage } from '../hooks/shared';
import { getLevelIcon } from '../lib/userUtils';
import { useUserStore } from '../stores';
import { Avatar, Card, CardContent } from './shared';

interface TeamMemberCardProps {
  user: User;
  onClick: () => void;
  isCurrentUser?: boolean;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({
  user,
  onClick,
  isCurrentUser = false,
}) => {
  const { t } = useLanguage();
  const userStore = useUserStore();

  const skillLevel = userStore.getUserSkillLevel(user.id);
  const skillLevelWeight = userStore.getUserSkillLevelWeight(user.id);
  const skillLevelIconId = userStore.getUserSkillLevelIconId(user.id);

  return (
    <Card
      className={`group cursor-pointer transition-all duration-200 hover:shadow-lg border ${
        isCurrentUser
          ? 'bg-blue-50 border-blue-200 shadow-sm'
          : 'bg-white hover:bg-gray-50'
      }`}
    >
      <CardContent className="p-4" onClick={onClick}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {/* Avatar */}
            <div className="relative">
              <Avatar
                src={user.photoUrl}
                alt={user.name}
                size="md"
                birthday={user.birthday}
                className={`flex-shrink-0 ${
                  isCurrentUser ? 'ring-2 ring-blue-300' : ''
                }`}
              />
              {isCurrentUser && (
                <div
                  className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center"
                  style={{ top: '-2px' }}
                >
                  <span className="text-white text-xs">✓</span>
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 min-w-0 mr-4">
              <div className="flex items-center space-x-2 mb-1">
                <h3
                  className={`font-semibold truncate ${
                    isCurrentUser ? 'text-blue-900' : 'text-gray-900'
                  }`}
                >
                  {user.name}
                </h3>
                {isCurrentUser && (
                  <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full font-medium">
                    {t('team.you')}
                  </span>
                )}
              </div>

              <div className="flex items-center space-x-1">
                <span className="text-base">
                  {getLevelIcon(skillLevelWeight, skillLevelIconId)}
                </span>
                <span
                  className={`text-sm ${
                    isCurrentUser ? 'text-blue-700' : 'text-gray-600'
                  }`}
                >
                  {skillLevel}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3 flex-shrink-0">
            {/* Statistics (WIP: muted and disabled) */}
            <div
              className="text-right pointer-events-none opacity-50 select-none"
              aria-disabled="true"
              title={t('common.inDevelopment')}
            >
              <div
                className={`text-lg font-bold ${
                  isCurrentUser ? 'text-blue-900' : 'text-gray-900'
                }`}
              >
                {'?'}
              </div>
              <div
                className={`text-xs leading-tight whitespace-pre-line text-right ${
                  isCurrentUser ? 'text-blue-600' : 'text-gray-500'
                }`}
              >
                {t('team.totalTrainings')}
              </div>
              {/* Subtle WIP hint */}
              <div className="mt-0.5 text-[10px] italic text-gray-400">
                {t('common.inDevelopment')}
              </div>
            </div>

            {/* Arrow indicator */}
            <div
              className={`transition-transform duration-200 group-hover:translate-x-0.5 ${
                isCurrentUser ? 'text-blue-500' : 'text-gray-400'
              }`}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TeamMemberCard;
